import { useQuery, useQueryClient } from 'react-query';
import { Http } from '../../services/Http';
import { MonthlyTimesheet, UserRole } from '../../types';
import { QUERY_KEYS } from '../constants';

interface Props {
  userId: number;
  yearQueryParam?: string;
  // Role-context of the page making the request. Multi-role users open several role-specific pages;
  // the API returns the dataset for THIS role (must be one the user holds) instead of their top role.
  role?: UserRole;
}

async function fetchMonthlyTimesheets(
  id: number,
  yearQueryParam?: string,
  role?: UserRole
): Promise<MonthlyTimesheet[]> {
  const params = new URLSearchParams();
  if (yearQueryParam) params.set('year', yearQueryParam);
  if (role) params.set('role', role);
  const qs = params.toString();
  const url = qs ? `/monthly-timesheet/user/${id}?${qs}` : `/monthly-timesheet/user/${id}`;
  const { data } = await Http.axios.get<MonthlyTimesheet[]>(url);
  return data;
}

export const useMonthlyTimesheets = ({ userId, yearQueryParam, role }: Props) => {
  return useQuery(
    [QUERY_KEYS.MONTHLY_TIMESHEET, userId, yearQueryParam, role],
    () => fetchMonthlyTimesheets(userId, yearQueryParam, role),
    {
      staleTime: Infinity,
      enabled: !!userId,
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
    }
  );
};

export const useInvalidateMonthlyTimesheets = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.MONTHLY_TIMESHEET,
    });
  };
};
